import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    TrendingUp,
    AlertTriangle,
    Inbox,
    Clock,
    Users,
    Sparkles,
    ArrowUpRight,
} from "lucide-react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";

function AnimatedNumber({ value, prefix = "", suffix = "" }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        let raf;
        const start = performance.now();
        const duration = 900;
        const animate = (t) => {
            const elapsed = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - elapsed, 3);
            setDisplay(Math.round(value * eased));
            if (elapsed < 1) raf = requestAnimationFrame(animate);
        };
        raf = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(raf);
    }, [value]);
    return <span>{prefix}{display.toLocaleString()}{suffix}</span>;
}

function KpiCard({ icon: Icon, label, value, accent, hint, testid }) {
    return (
        <div
            data-testid={testid}
            className="elate-card p-6 relative overflow-hidden group hover:-translate-y-1 hover:border-white/20 transition-all duration-300"
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                <Icon className="h-4 w-4" style={{ color: accent }} strokeWidth={1.6} />
            </div>
            <div className="font-display text-4xl mt-3 font-medium tracking-tight">
                {value}
            </div>
            {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard/stats").then(({ data }) => {
            setStats(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Overview</div>
                    <h2 className="font-display text-3xl mt-2 tracking-tight">Welcome back.</h2>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-400 font-mono">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 elate-breathe" />
                    Live · synced just now
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="elate-card p-6 animate-pulse h-32" />
                    ))}
                </div>
            ) : stats && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        <KpiCard
                            testid="kpi-mrr"
                            icon={TrendingUp}
                            label="Monthly recurring revenue"
                            value={<AnimatedNumber value={Math.round(stats.mrr)} prefix="£" />}
                            accent="#0066FF"
                            hint={`${stats.clients_total} active clients`}
                        />
                        <KpiCard
                            testid="kpi-leads"
                            icon={Inbox}
                            label="New leads today"
                            value={<AnimatedNumber value={stats.leads_new_today} />}
                            accent="#FF5500"
                            hint={`${stats.leads_total} total`}
                        />
                        <KpiCard
                            testid="kpi-overdue"
                            icon={Clock}
                            label="Tasks overdue"
                            value={<AnimatedNumber value={stats.tasks_overdue} />}
                            accent="#F59E0B"
                            hint={`${stats.tasks_open} open`}
                        />
                        <KpiCard
                            testid="kpi-expiring"
                            icon={AlertTriangle}
                            label="Domains expiring"
                            value={<AnimatedNumber value={stats.clients_expiring} />}
                            accent="#EF4444"
                            hint="Next 30 days"
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        {/* Expiring soon */}
                        <div className="lg:col-span-2 elate-card p-6">
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Needs attention</div>
                                    <h3 className="font-display text-lg mt-1">Clients expiring soon</h3>
                                </div>
                                <Link to="/app/clients" data-testid="view-all-clients" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
                                    View all <ArrowUpRight className="h-3 w-3" />
                                </Link>
                            </div>
                            <div className="space-y-2">
                                {stats.expiring_clients.length === 0 && (
                                    <div className="text-sm text-zinc-500 py-8 text-center">All clean. No renewals this month.</div>
                                )}
                                {stats.expiring_clients.map((c) => (
                                    <motion.div
                                        key={c.id}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 hover:bg-white/[0.04] transition"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <img src={c.logo} alt="" className="h-8 w-8 rounded-lg object-cover" />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">{c.name}</div>
                                                <div className="text-xs text-zinc-500 truncate">{c.website}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span
                                                className={`dot ${
                                                    c.renewal_status === "critical" ? "dot-crit"
                                                        : c.renewal_status === "warning" ? "dot-warn"
                                                        : "dot-ok"
                                                }`}
                                            />
                                            <div className="text-xs text-zinc-400 font-mono">{c.domain_expires_in_days}d</div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Recent leads */}
                        <div className="elate-card p-6">
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Inbox</div>
                                    <h3 className="font-display text-lg mt-1">Recent leads</h3>
                                </div>
                                <Link to="/app/leads" data-testid="view-all-leads" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
                                    Open <ArrowUpRight className="h-3 w-3" />
                                </Link>
                            </div>
                            <div className="space-y-2">
                                {stats.recent_leads.length === 0 && (
                                    <div className="text-sm text-zinc-500 py-8 text-center">No leads yet.</div>
                                )}
                                {stats.recent_leads.map((l) => (
                                    <div key={l.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-medium">{l.name}</div>
                                            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{l.ad_platform}</div>
                                        </div>
                                        <div className="text-xs text-zinc-500 mt-1 truncate">{l.campaign}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* AI banner */}
                    <div className="elate-card p-6 relative overflow-hidden elate-ai-border">
                        <div className="flex items-start gap-4">
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#FF5500] grid place-items-center">
                                <Sparkles className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Ana</div>
                                <div className="font-display text-lg mt-1">Need to see the whole pipeline?</div>
                                <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                                    Press <kbd className="font-mono bg-white/[0.06] px-1.5 py-0.5 rounded text-xs">⌘K</kbd> and ask
                                    things like &quot;show clients with expiring domains&quot;, &quot;which leads need chasing today?&quot;
                                    or &quot;create a summer campaign for Cedar Views&quot;.
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
